'use client';

import { useEffect, useRef, useState } from 'react';
import { PortableText } from '@portabletext/react';
import { PortableTextBlock } from 'sanity';
import { motion } from 'framer-motion';

import { GroqImage } from '@/helpers/Image';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { IMentorProfile } from '@/sanity/pages/PageMentorsTemplate';
import { SanityVideoPlayer } from '@/components/ui/SanityVideoPlayer';
import { CTAButton } from '@/shadcn/ui/button';
import type { IPageLink } from '@/sanity/globals/CTA';

import { PipeChip } from '../home/_shared';
import { FadeIn } from '@/components/global/utils/FadeIn';

interface MentorDetailsSectionProps {
  chip?: string;
  title?: PortableTextBlock[];
  mentors?: IMentorProfile[];
}

export const mentorProfileAnchorId = (id: string) => `mentor-${id}`;

const cardId = (mentor: IMentorProfile, i: number) => mentorProfileAnchorId(mentor._id ?? String(i));

function MentorCard({ mentor, id }: { mentor: IMentorProfile; id: string }) {
  return (
    <article
      id={id}
      className="flex flex-col items-start bg-white/[0.04] border border-white/[0.06] rounded-[1rem] p-[3rem] gap-[4rem] lg:p-[4rem] lg:gap-[4rem] w-full scroll-mt-[16rem] lg:scroll-mt-[10rem]"
    >
      <header className="flex items-center gap-[2rem]">
        {mentor.avatar && (
          <div className="overflow-hidden relative rounded-full shrink-0 w-[7.25rem] h-[7.25rem] lg:w-[8.25rem] lg:h-[8.25rem]">
            <GroqImage
              src={mentor.avatar}
              alt={mentor.name || ''}
              className="!object-cover !w-full !h-full"
            />
          </div>
        )}
        <div className="flex flex-col gap-[1.25rem]">
          <p className="m-0 leading-[1.1] font-news text-[3.5rem] tracking-[-0.25rem] lg:text-[4.25rem] text-white/90">
            {mentor.name}
          </p>
          {mentor.role && (
            <p className="m-0 leading-[1.5] font-geist text-[2.25rem] text-white/50">
              {mentor.role}
            </p>
          )}
        </div>
      </header>

      <div className="w-full h-px bg-white/10" />

      {mentor.description && (
        <div className="richtext richtext-start">
          <PortableText
            value={mentor.description}
            components={richTextComponent}
          />
        </div>
      )}

      {(mentor.video?.muxVideo?.playbackId || mentor.video?.embedUrl) && (
        <div className="overflow-hidden relative w-full rounded-[0.5rem] lg:rounded-[1rem] aspect-[872/460] bg-black">
          <SanityVideoPlayer
            className="!w-full !h-full !object-cover"
            playerData={mentor.video}
            playsinline
          />
        </div>
      )}

      {mentor.ctaTitle && (
        <div className="w-full lg:w-auto lg:self-start">
          <CTAButton
            data={{
              cta: {
                name: '',
                title: mentor.ctaTitle,
                icon: 'arrowRight',
                action: 'link',
                link: {
                  externalLink: true,
                  url: mentor.ctaLink,
                } as IPageLink,
              },
            }}
            fullWidth
          />
        </div>
      )}
    </article>
  );
}

function NavPill({
  name,
  isActive,
  layoutId,
}: {
  name?: string;
  isActive: boolean;
  layoutId: string;
}) {
  return (
    <>
      {isActive && (
        <motion.span
          layoutId={layoutId}
          className="absolute inset-0 rounded-[0.5rem] bg-white/[0.08]"
          transition={{ type: 'spring', stiffness: 400, damping: 40 }}
        />
      )}

      <span className="relative shrink-0 block w-[0.75rem] h-[0.75rem]">
        <motion.span
          className="block w-full h-full bg-[#98d8ff]"
          initial={false}
          animate={{
            opacity: isActive ? 1 : 0,
            scale: isActive ? 1 : 0.4,
            rotate: 45,
          }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        />
      </span>

      <motion.span
        className="relative font-geist text-[2.25rem] leading-[1.5] text-white whitespace-nowrap"
        initial={false}
        animate={{ opacity: isActive ? 1 : 0.4 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        {name}
      </motion.span>
    </>
  );
}

function DesktopNav({
  mentors,
  activeIndex,
}: {
  mentors: IMentorProfile[];
  activeIndex: number;
}) {
  return (
    <nav className="hidden lg:flex flex-col shrink-0 sticky top-[12rem] self-start w-[24rem]">
      {mentors.map((m, i) => (
        <a
          key={m._id ?? i}
          href={`#${cardId(m, i)}`}
          className="relative flex items-center gap-[1.5rem] rounded-[0.5rem] p-[1.5rem]"
        >
          <NavPill
            name={m.name}
            isActive={i === activeIndex}
            layoutId="mentor-nav-desktop-bg"
          />
        </a>
      ))}
    </nav>
  );
}

function MobileNav({
  mentors,
  activeIndex,
}: {
  mentors: IMentorProfile[];
  activeIndex: number;
}) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLAnchorElement | null>>([]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    const el = itemRefs.current[activeIndex];
    if (!scroller || !el) return;
    const offset =
      el.offsetLeft - scroller.clientWidth / 2 + el.clientWidth / 2;
    scroller.scrollTo({ left: offset, behavior: 'smooth' });
  }, [activeIndex]);

  return (
    <div className="lg:hidden sticky top-[2rem] z-40 w-full">
      <div
        ref={scrollerRef}
        className="overflow-x-auto scrollbar-hide bg-[#080808]/95 backdrop-blur-md rounded-[0.75rem] p-[0.75rem]"
      >
        <div className="flex items-center gap-[0.25rem] w-max">
          {mentors.map((m, i) => (
            <a
              key={m._id ?? i}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              href={`#${cardId(m, i)}`}
              className="relative flex items-center gap-[1.5rem] rounded-[0.5rem] px-[2rem] py-[1.5rem]"
            >
              <NavPill
                name={m.name}
                isActive={i === activeIndex}
                layoutId="mentor-nav-mobile-bg"
              />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MentorDetailsSection({
  chip,
  title,
  mentors,
}: MentorDetailsSectionProps) {
  const items = mentors ?? [];
  const [activeIndex, setActiveIndex] = useState(0);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    if (items.length === 0) return;

    const handler = () => {
      const anchor = window.innerHeight * 0.3;
      let current = 0;
      for (let i = 0; i < cardRefs.current.length; i++) {
        const el = cardRefs.current[i];
        if (!el) continue;
        if (el.getBoundingClientRect().top <= anchor) current = i;
        else break;
      }
      setActiveIndex(current);
    };

    handler();
    window.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler);
      window.removeEventListener('resize', handler);
    };
  }, [items.length]);

  if (items.length === 0) return null;

  return (
    <section className="overflow-x-clip flex flex-col items-center w-full bg-black pt-[8rem] pb-[10rem] gap-[4rem] px-[2rem] lg:pt-[15rem] lg:pb-[20rem] lg:gap-[8rem] lg:px-[8rem]">
      <div className="flex flex-col items-center text-center gap-[3rem] w-[min(74.25rem,100%)]">
        {chip && (
          <FadeIn delay={0}>
            <PipeChip>{chip}</PipeChip>
          </FadeIn>
        )}
        {title && (
          <FadeIn delay={0.1} className="richtext">
            <PortableText value={title} components={richTextComponent} />
          </FadeIn>
        )}
      </div>

      <MobileNav mentors={items} activeIndex={activeIndex} />

      <div className="flex justify-between items-start w-full max-w-[150rem] gap-0 lg:gap-[4rem]">
        <DesktopNav mentors={items} activeIndex={activeIndex} />

        <div className="flex flex-col items-end gap-[2rem] lg:gap-[2.75rem] w-full lg:w-[min(115rem,100%)]">
          {items.map((mentor, i) => (
            <div
              key={mentor._id ?? i}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
              data-mentor-index={i}
              className="w-full"
            >
              <MentorCard mentor={mentor} id={cardId(mentor, i)} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
